import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchResultsSorting } from '@steelbuy/ui-domain';
import { ButtonTertiaryOnLightM, IconIdentifier } from '@steelbuy/ui-primitive';
import { DashboardFilter } from './DashboardFilter';
import { useDashboardFormContext } from './DashboardFormContext';
import { RoutePath } from '../../router/Routes';
import { SORT_OPTIONS } from '../common-utils/ListingTableUtils';
import { ListingsTable, ListingsTableProps } from '../listings-table/ListingsTable';

type DashboardListingsResultsProps = Omit<ListingsTableProps, 'checkoutPath' | 'detailsPath'>;

const keepFields = new Set(['materialType', 'definition', 'sort']);

export const DashboardListingsResults = ({
    total,
    listings,
    formData,
    hasNextPage,
    loadMore,
    isFetching,
    isFetchingNextPage,
    isInitialLoading,
}: DashboardListingsResultsProps) => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const { toggleFilter, isFilterShown } = useDashboardFormContext();
    const { setValue, watch, getValues, unregister } = useFormContext();

    return (
        <div className="dashboard-listings-results">
            <div className="dashboard-listings-results-header">
                <div className="dashboard-listings-results-header__results">
                    {total === undefined ? '' : t('application.dashboardListings.results.header', { count: total })}
                </div>
                <div
                    className={classNames('dashboard-listings-results-header__actions', {
                        'dashboard-listings-results-header__actions--filter-opened': isFilterShown,
                    })}
                >
                    <ButtonTertiaryOnLightM
                        label={t('application.dashboardListings.filter.buttonLabel')}
                        icon={IconIdentifier.FILTER}
                        onClick={() => {
                            toggleFilter();
                            if (isFilterShown) {
                                Object.keys(getValues()).forEach((name) => {
                                    if (!keepFields.has(name)) {
                                        unregister(name);
                                    }
                                });
                            }
                        }}
                    />
                    <SearchResultsSorting
                        handleSortChange={(value) => setValue('sort', value)}
                        sortCriteria={watch('sort')}
                        isDisabled={total === 0}
                        selectedLabelMapper={(option) =>
                            t(`application.dashboardListings.sortByOption`, { option: option.label })
                        }
                        showTitle={false}
                        sortOptions={SORT_OPTIONS}
                    />
                </div>
            </div>
            {isFilterShown && <DashboardFilter />}
            <ListingsTable
                listings={listings}
                formData={formData}
                hasNextPage={hasNextPage}
                loadMore={loadMore}
                isFetching={isFetching}
                isFetchingNextPage={isFetchingNextPage}
                total={total}
                detailsPath={RoutePath.DASHBOARD_LISTING_DETAILS}
                isInitialLoading={isInitialLoading}
            />
        </div>
    );
};
